import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from 'sonner';
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { Company } from "@/types/company";

export const DataExportSettings = () => {
  const t = useTranslations('consultantSettings.export');
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);

  const exportService = ServiceRegistry.getSie4ExportService();
  const companyService = ServiceRegistry.getCompanyService();

  useEffect(() => {
    companyService.getCompany()
      .then(setCompany)
      .catch((error) => {
        console.error('Error loading company:', error);
        toast.error(t('loadFailed'));
      });
  }, []);

  const handleExport = async () => {
    if (!company) return;

    try {
      setLoading(true);
      await exportService.exportCompanyData(company.name);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          {t('title')}
        </CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground">
          {t('body')}
        </p>

        {company && (
          <div className="text-sm text-muted-foreground">
            {t.rich('company', { name: company.name, strong: (chunks) => <strong>{chunks}</strong> })}
            {company.organization_number && (
              <> {t('organizationNumber', { number: company.organization_number })}</>
            )}
          </div>
        )}

        <Button
          onClick={handleExport}
          disabled={loading || !company}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('generating')}
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              {t('action')}
            </>
          )}
        </Button>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>{t.rich('includedTitle', { strong: (chunks) => <strong>{chunks}</strong> })}</p>
          <ul className="list-disc list-inside pl-2">
            <li>{t('includedFiscalYears')}</li>
            <li>{t('includedChart')}</li>
            <li>{t('includedEntries')}</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
